import dayjs, { Dayjs } from 'dayjs';
import relativeTime from 'dayjs/plugin/relativeTime';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { TimeFormats } from 'src/graphql';
import { DraftScheduleSendUnless } from 'src/pages/Conversations/Chat/conversation-draft-schedule';
import { useDisplayTime } from 'src/utils/hooks';
import {
  ensureFutureDate,
  getDateFromNaturalLanguage,
  resolveNaturalLanguageDate,
} from 'src/utils/natural-language-date';
import { SendUnlessOption, useSendUnlessConditions } from './use-send-unless-conditions';

dayjs.extend(relativeTime);

interface SchedulePreset {
  id: string;
  label: string;
  compute: () => Dayjs;
}

export interface ScheduleListItem {
  id: string;
  primary: string;
  secondary: string;
  onSelect: () => void;
  selected: boolean;
  scheduledAt?: string | null;
}

const SCHEDULE_PRESETS: SchedulePreset[] = [
  {
    id: 'in-hour',
    label: 'An hour from now',
    compute: () => dayjs().add(1, 'hour').second(0).millisecond(0),
  },
  {
    id: 'tomorrow-morning',
    label: 'Tomorrow',
    compute: () =>
      dayjs()
        .add(1, 'day')
        .set('hour', 9)
        .set('minute', 0)
        .set('second', 0)
        .set('millisecond', 0),
  },
  {
    id: 'next-week',
    label: 'Next week',
    compute: () =>
      dayjs()
        .add(1, 'week')
        .set('hour', 9)
        .set('minute', 0)
        .set('second', 0)
        .set('millisecond', 0),
  },
];

const MINUTE_STEP = 5;
const DEFAULT_DAY_START = '9:00am';

export interface ScheduleSendModalState {
  inputValue: string;
  placeholder: string;
  listItems: ScheduleListItem[];
  hasNoResults: boolean;
  selectedDate: Dayjs | null;
  selectedDateLabel: string;
  customDate: Dayjs;
  isPickerOpen: boolean;
  isValid: boolean;
  options: SendUnlessOption[];
  toggleCondition: (type: DraftScheduleSendUnless['type']) => void;
  minutesStep: number;
  onInputChange: (value: string) => void;
  openPicker: () => void;
  closePicker: () => void;
  setCustomDate: (value: Dayjs | null) => void;
  commitCustomDate: () => void;
  buildSubmitPayload: (scheduledAtOverride?: string | null) => {
    scheduledAt: string;
    sendUnless: DraftScheduleSendUnless[];
  } | null;
}

export function useScheduleSendModalState(params: {
  open: boolean;
  initialDate?: string | null;
  initialConditions?: DraftScheduleSendUnless[] | null;
}): ScheduleSendModalState {
  const { open, initialDate, initialConditions } = params;
  const [inputValue, setInputValue] = useState('');
  const [selectedDate, setSelectedDate] = useState<Dayjs | null>(null);
  const [customDate, setCustomDate] = useState<Dayjs>(() => dayjs().add(1, 'hour'));
  const [isPickerOpen, setIsPickerOpen] = useState(false);
  const [selectedItemId, setSelectedItemId] = useState<string>('custom');

  const { displayTime, timeFormatPref } = useDisplayTime();
  const {
    options,
    selectedConditions,
    toggleCondition,
    hydrate: hydrateConditions,
  } = useSendUnlessConditions(initialConditions);

  const resolveInitialSelectionId = useCallback((value: Dayjs | null): string => {
    if (!value) {
      return 'custom';
    }

    for (const preset of SCHEDULE_PRESETS) {
      const computed = preset.compute();
      if (value.isSame(computed, 'minute')) {
        return preset.id;
      }
    }

    return 'custom';
  }, []);

  useEffect(() => {
    if (!open) {
      return;
    }

    const nextDate = initialDate ? dayjs(initialDate) : dayjs().add(1, 'hour');
    setSelectedDate(nextDate);
    setCustomDate(nextDate);
    setInputValue('');
    hydrateConditions(initialConditions ?? null);
    setSelectedItemId(resolveInitialSelectionId(nextDate));
  }, [hydrateConditions, initialConditions, initialDate, open, resolveInitialSelectionId]);

  const placeholder = useMemo(() => {
    return `Try ${timeFormatPref === TimeFormats.TwelveHour ? '8am' : '20:00'}, in 3 days, Aug 16th`;
  }, [timeFormatPref]);

  const handleInputChange = useCallback((value: string): void => {
    setInputValue(value);
    setSelectedItemId('custom');
  }, []);

  const selectDate = useCallback(
    (value: Dayjs, sourceId = 'custom', options?: { preserveInput?: boolean }): void => {
      setSelectedDate(value);
      setCustomDate(value);
      setSelectedItemId(sourceId);
      setIsPickerOpen(false);
      if (!options?.preserveInput) {
        setInputValue('');
      }
    },
    [],
  );

  const openPicker = useCallback((): void => {
    setSelectedItemId('custom');
    setIsPickerOpen(true);
  }, []);

  const closePicker = useCallback((): void => {
    setIsPickerOpen(false);
    if (selectedDate) {
      setCustomDate(selectedDate);
    }
  }, [selectedDate]);

  const naturalLanguageIso = useMemo(() => {
    if (!inputValue.trim()) {
      return '';
    }
    const parsedWithDayStart = getDateFromNaturalLanguage(inputValue, {
      dayStart: DEFAULT_DAY_START,
    });
    if (!parsedWithDayStart) {
      return '';
    }
    const parsedWithoutDayStart = getDateFromNaturalLanguage(inputValue);
    const resolved = resolveNaturalLanguageDate(
      parsedWithDayStart,
      parsedWithoutDayStart || parsedWithDayStart,
    );
    return ensureFutureDate(resolved, inputValue);
  }, [inputValue]);

  const typedInputDate = useMemo(() => {
    if (!naturalLanguageIso) {
      return null;
    }

    const parsed = dayjs(naturalLanguageIso);
    return parsed.isValid() ? parsed : null;
  }, [naturalLanguageIso]);

  const selectIsoDate = useCallback(
    (iso: string, sourceId: string): void => {
      const parsed = dayjs(iso);
      if (!parsed.isValid()) {
        return;
      }
      const preserveInput = sourceId === 'natural-language';
      selectDate(parsed, sourceId, { preserveInput });
    },
    [selectDate],
  );

  useEffect(() => {
    if (!typedInputDate) {
      return;
    }

    selectDate(typedInputDate, 'natural-language', { preserveInput: true });
  }, [selectDate, typedInputDate]);

  const formatListDescription = useCallback(
    (value: Dayjs): string => {
      const dayPart = value.format('ddd, MMMM D');
      const timePart = displayTime(value);
      return `${dayPart} at ${timePart}`;
    },
    [displayTime],
  );

  const defaultItems = useMemo(() => {
    return SCHEDULE_PRESETS.map((preset) => {
      const computed = preset.compute();
      return {
        id: preset.id,
        // Prefix all date-time presets with "Send "
        primary: `Send ${preset.label.toLowerCase()}`,
        secondary: formatListDescription(computed),
        onSelect: () => selectDate(computed, preset.id),
        selected: selectedItemId === preset.id,
        scheduledAt: computed.toISOString(),
      };
    });
  }, [formatListDescription, selectDate, selectedItemId]);

  const customItem = useMemo<ScheduleListItem>(() => {
    const label = selectedDate ? formatListDescription(selectedDate) : 'Select date and time';
    return {
      id: 'custom',
      primary: 'Custom',
      secondary: label,
      onSelect: openPicker,
      selected: selectedItemId === 'custom',
      scheduledAt: selectedDate ? selectedDate.toISOString() : null,
    };
  }, [formatListDescription, openPicker, selectedDate, selectedItemId]);

  const listItems = useMemo(() => {
    if (inputValue.trim()) {
      if (!typedInputDate) {
        return [] as ScheduleListItem[];
      }
      return [
        {
          id: 'natural-language',
          // Prefix natural language options with "Send "
          primary: `Send ${typedInputDate.fromNow().toLowerCase()}`,
          secondary: formatListDescription(typedInputDate),
          onSelect: () => selectIsoDate(naturalLanguageIso, 'natural-language'),
          selected: selectedItemId === 'natural-language',
          scheduledAt: typedInputDate.toISOString(),
        },
      ];
    }

    return [...defaultItems, customItem];
  }, [
    customItem,
    defaultItems,
    formatListDescription,
    inputValue,
    naturalLanguageIso,
    selectIsoDate,
    selectedItemId,
    typedInputDate,
  ]);

  const commitCustomDate = useCallback((): void => {
    if (!customDate) {
      return;
    }
    selectDate(customDate, 'custom');
  }, [customDate, selectDate]);

  const isValid = useMemo(() => {
    return Boolean(selectedDate?.isAfter(dayjs()));
  }, [selectedDate]);

  const selectedDateLabel = useMemo(() => {
    return selectedDate ? formatListDescription(selectedDate) : '';
  }, [formatListDescription, selectedDate]);

  const hasNoResults = Boolean(inputValue.trim() && !naturalLanguageIso);

  const buildSubmitPayload = useCallback(
    (
      scheduledAtOverride?: string | null,
    ): {
      scheduledAt: string;
      sendUnless: DraftScheduleSendUnless[];
    } | null => {
      const target = scheduledAtOverride ?? selectedDate?.toISOString() ?? null;
      if (!target) {
        return null;
      }

      return {
        scheduledAt: target,
        sendUnless: selectedConditions.map(
          (condition) => ({ ...condition }) as DraftScheduleSendUnless,
        ),
      };
    },
    [selectedConditions, selectedDate],
  );

  const setCustomDateIfSet = useCallback((value: Dayjs | null): void => {
    if (value) {
      setCustomDate(value);
    }
  }, []);

  return useMemo(
    () =>
      ({
        inputValue,
        placeholder,
        listItems,
        hasNoResults,
        selectedDate,
        selectedDateLabel,
        customDate,
        isPickerOpen,
        isValid,
        options,
        toggleCondition,
        minutesStep: MINUTE_STEP,
        onInputChange: handleInputChange,
        openPicker,
        closePicker,
        setCustomDate: setCustomDateIfSet,
        commitCustomDate,
        buildSubmitPayload,
      }) satisfies ScheduleSendModalState,
    [
      inputValue,
      placeholder,
      listItems,
      hasNoResults,
      selectedDate,
      selectedDateLabel,
      customDate,
      isPickerOpen,
      isValid,
      options,
      toggleCondition,
      handleInputChange,
      openPicker,
      closePicker,
      setCustomDateIfSet,
      commitCustomDate,
      buildSubmitPayload,
    ],
  );
}
